import { DecisionTool } from "../../generated/backend/v1/plan";
import { compareDates, formatMonth } from "../lib/dates";
import { money } from "../lib/format";
import type { DecisionFacts } from "./verdict";

/**
 * The words of a decision's verdict line, in one place so they can be reworded without touching
 * the rule (verdict.ts) or the page (ScenarioResults.tsx): a bold `head`, then the `rest`. The
 * internal verdict values (`yes`, `trade`, `no`) never reach the page.
 */
export type VerdictText = { head: string; rest: string };

const monthOf = (x: { year: number; month: number }) => ({ year: x.year, month: x.month, day: 0 });
const pct = (v: number) => `${Math.round(v * 100)}%`;

/** After a verdict where the scenario doesn't run short: whether it clears today's shortfall. */
function clears(f: DecisionFacts): string {
  const today = f.shortfallBefore;
  return today && !f.shortfall
    ? ` This clears your shortfall: your plan no longer runs short (today it does, in ${formatMonth(today.date)}).`
    : "";
}

/**
 * Both plans run short, the scenario's no sooner and no larger (`either`, amber): the head says
 * whether it postpones the shortfall, narrows the gap, both, or neither; the rest gives the months
 * and amounts, then what else changes before then (left over each month, a priority).
 */
function eitherWay(f: DecisionFacts): VerdictText {
  const now = f.shortfall!;
  const today = f.shortfallBefore!;
  const e = f.either!;
  const when = formatMonth(now.date);
  const was = formatMonth(today.date);
  const amt = money(now.amount);
  const wasAmt = money(today.amount);
  let head: string;
  let rest: string;
  if (e.later && e.smaller) {
    head = "Your plan runs short either way, but this postpones it and narrows the gap.";
    rest = `It runs short in ${when} instead of ${was}, and by about ${amt} instead of ${wasAmt}.`;
  } else if (e.later) {
    head = "Your plan runs short either way, but this postpones it.";
    rest =
      `It runs short in ${when} instead of ${was}.` +
      (e.larger ? ` It’s short by more then: about ${amt}, instead of ${wasAmt}.` : "");
  } else if (e.smaller) {
    head = "Your plan runs short either way, but this narrows the gap.";
    rest = `It still runs short in ${when}, but by about ${amt} instead of ${wasAmt}.`;
  } else {
    head = "Your plan runs short either way, as it does today.";
    rest = `It runs short in ${when}, about ${amt} short that month.`;
  }
  const lo = f.leftOver;
  const window = f.months < 12 ? "before then" : "in the first year";
  const diff = Math.round(lo.after) - Math.round(lo.before);
  if (f.months && Math.abs(diff) >= 1)
    rest += ` It leaves you ${money(Math.abs(diff))} a month ${diff < 0 ? "less" : "more"} ${window}.`;
  if (f.lost) rest += ` ${f.lost.name} goes from ${pct(f.lost.before)} funded to nothing.`;
  else if (f.givesWay) rest += ` ${f.givesWay.name} drops to ${pct(f.givesWay.after)} funded.`;
  return { head, rest };
}

/**
 * Insurance asks a different question ("if it happens, does this coverage hold the plan up?"), so
 * its heads say covered or not; the rest of the line, and the rule, are the same as every module's.
 */
const INSURANCE_HEAD: Partial<Record<DecisionFacts["reason"], string>> = {
  shortfall: "Not covered: your plan runs short if it happens.",
  lost: "Not covered: a priority goes unfunded if it happens.",
  priority: "Partly covered: something gives if it happens.",
  retirement: "Partly covered: retirement moves if it happens.",
  none: "Covered.",
};

export function verdictText(f: DecisionFacts, tool?: DecisionTool): VerdictText {
  const t = baseText(f);
  const head = tool === DecisionTool.INSURANCE ? INSURANCE_HEAD[f.reason] : undefined;
  return head ? { ...t, head } : t;
}

function baseText(f: DecisionFacts): VerdictText {
  const ret = f.retirement.after;
  const still = clears(f);
  if (f.reason === "shortfall" && f.shortfall)
    return {
      head: "You can’t afford this.",
      rest:
        `With it, your plan runs short in ${formatMonth(f.shortfall.date)}: about ${money(f.shortfall.amount)} more goes out than comes in that month. ` +
        (!f.shortfallBefore
          ? "Today it doesn’t run short at all."
          : compareDates(monthOf(f.shortfallBefore.date), monthOf(f.shortfall.date)) > 0
            ? `Today it runs short later, in ${formatMonth(f.shortfallBefore.date)}.`
            : `Today it runs short that month too, but by less: about ${money(f.shortfallBefore.amount)}.`),
    };
  if (f.reason === "either" && f.shortfall && f.shortfallBefore && f.either) return eitherWay(f);
  if (f.reason === "lost" && f.lost)
    return {
      head: "Not without giving up a priority.",
      rest: `${f.lost.name} goes from ${pct(f.lost.before)} funded to nothing.${still}`,
    };
  if (f.reason === "priority" && f.givesWay)
    return {
      head: "Yes, but something gives.",
      rest:
        `${f.givesWay.name} drops to ${pct(f.givesWay.after)} funded` +
        (f.retirement.monthsLater >= 1 && ret
          ? `, and retirement moves to ${formatMonth(ret.date)}`
          : "") +
        `.${still}`,
    };
  if (f.reason === "retirement" && ret && f.retirement.before)
    return {
      head: "Yes, but retirement moves.",
      rest: `It moves from ${formatMonth(f.retirement.before.date)} to ${formatMonth(ret.date)}; every priority keeps its funding.${still}`,
    };
  return {
    head: "Yes.",
    rest:
      (f.improves
        ? `Your plan is better off: ${f.improves.name} rises to ${pct(f.improves.after)} funded, and nothing loses funding.`
        : `Your plan absorbs it: no priority loses funding${ret ? " and retirement stays where it is" : ""}.`) +
      still,
  };
}
